import React, { useState, useEffect, useRef, useCallback } from "react";
import { saveAs } from "file-saver";
import {
  compareCodigos,
  determineFileName,
  performReplacementUsingPair,
  highlightDifferences,
} from "./DiffService";


const CodeDiffModal = ({ code1, code2 }) => {
  const [showModal, setShowModal] = useState(false);
  const [originalCode, setOriginalCode] = useState({ linhas: [] });
  const [modifiedCode, setModifiedCode] = useState({ linhas: [] });
  const [diffPairs, setDiffPairs] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const modalContentRef = useRef(null);

  useEffect(() => {
    if (code1 || code2) {
      generateDiff();
    }
  }, [code1, code2]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target)
      ) {
        closeModal();
      }
    };
  
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
  
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []); 

  const closeModal = () => {
    setShowModal(false);
    setSuccessMessage(""); // Limpa a mensagem de sucesso ao fechar o modal
  };

  const openModal = () => setShowModal(true);

  const generateDiff = () => {
    if (!code1 || !code2) {
      console.error("Code strings are undefined");
      return;
    }

    const processed = compareCodigos(code1, code2);
    setDiffPairs(processed.pl);
    setOriginalCode(processed.original);
    setModifiedCode(processed.modified);
  };

  const isFirstInGroup = (index, type) => {
    const relevantLines =
      type === "original" ? originalCode.linhas : modifiedCode.linhas;
    const currentLineNum = relevantLines[index].numero;

    return diffPairs.some((pair) => {
      const lines = type === "original" ? pair.original : pair.modified;
      return lines.length > 0 && lines[0].numero === currentLineNum;
    });
  };

  const copyToClipboard = (code) => {
    const textToCopy = code.linhas.map((linha) => linha.linha).join("\n");
    navigator.clipboard.writeText(textToCopy).then(() => {
      //Todo: O que faze após escrever o texto no clipboard?
    });
  };

  const replaceContent = (originalIndex, modifiedIndex, direction) => {
    let pair;
    if (direction === "left") {
      const modifiedLineNumber = modifiedCode.linhas[modifiedIndex]?.numero;
      pair = diffPairs.find((p) =>
        p.modified.some((line) => line.numero === modifiedLineNumber)
      );
    } else {
      const originalLineNumber = originalCode.linhas[originalIndex]?.numero;
      pair = diffPairs.find((p) =>
        p.original.some((line) => line.numero === originalLineNumber)
      );
    }

    if (!pair) {
      console.error("Corresponding pair not found");
      return;
    }

    if (direction === "left") {
      performReplacementUsingPair(originalCode, pair.original, pair.modified);
    } else {
      performReplacementUsingPair(modifiedCode, pair.modified, pair.original);
    }

    updateCodeStrings();
  };

  const deleteLine = (line, type) => {
    const code = type === "original" ? originalCode : modifiedCode;
    code.linhas = code.linhas.filter((l) => l.numero !== line.numero);
    updateCodeStrings();
  };

  const updateCodeStrings = () => {
    const originalFirstLine = code1.split("\n")[0];
    const modifiedFirstLine = code2.split("\n")[0];

    code1 = [
      originalFirstLine,
      ...originalCode.linhas.map((linha) => linha.linha),
    ].join("\n");
    code2 = [
      modifiedFirstLine,
      ...modifiedCode.linhas.map((linha) => linha.linha),
    ].join("\n");
    generateDiff();
  };

  const downloadCode = (code, baseName) => {
    const fileName = removeTxtExtension(determineFileName(code, baseName));
    console.log("filename: " + fileName);
    const codeContent = code.linhas.map((linha) => linha.linha).join("\n");
    const blob = new Blob([codeContent], {
      type: localStorage.getItem("devMode") + "; charset=utf-8",
    });
    saveAs(blob, fileName);
  
    // Exibir mensagem de sucesso
    setSuccessMessage("Arquivo enviado com sucesso!");
    setTimeout(() => setSuccessMessage(""), 3000); // Remove a mensagem após 3 segundos
  };

  //QuickFix:
  function removeTxtExtension(text) {
    if (text.endsWith(".txt")) {
      if (text.endsWith(".txt.txt")) {
        return text.slice(0, -4);
      } else {
        return text.slice(0, -4);
      }
    }
    return text;
  }

  return (
    <div>
      <button
        onClick={openModal}
        className="btn btn-primary text-white rounded px-4"
      >
        Diff
      </button>
      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}
      {showModal && (
        <div
          className="modal fade show d-block m-0"
          tabIndex="-1"
          role="dialog"
        >
          <div className="w-100 h-100 position-fixed bg-dark opacity-75"></div>
          <div
            className="modal-dialog"
            style={{ maxWidth: "80%" }}
            role="document"
          >
            <div className="modal-content" ref={modalContentRef}>
              <div className="modal-header">
                <h5 className="modal-title">Code Diff</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h4 className="font-bold mb-2 d-flex justify-content-between align-items-center">
                      Original Code
                      <div className="d-flex gap-3">
                        <button
                          onClick={() => copyToClipboard(originalCode)}
                          className="btn btn-secondary text-white btn-sm rounded"
                        >
                          Copiar
                        </button>
                        <button
                          onClick={() =>
                            downloadCode(
                              originalCode,
                              "original." + localStorage.getItem("devEXT")
                            )
                          }
                          className="btn btn-primary text-white btn-sm rounded"
                        >
                          Download Original
                        </button>
                      </div>
                    </h4>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <tbody className="original-body">
                          {originalCode.linhas.map((linha, i) => {
                            const modLine = modifiedCode.linhas[i] || {
                              linha: "",
                            }; // Ensuring a fallback

                            let content;
                            if (
                              linha.tipo === "original" ||
                              linha.tipo === "modified"
                            ) {
                              const { highlightedOriginal } =
                                highlightDifferences(
                                  linha.linha,
                                  modLine.linha
                                );
                              content = (
                                <code
                                  className="d-block text-start font-monospace flex-grow-1 text-black"
                                  style={{ whiteSpace: "pre-wrap" }}
                                  dangerouslySetInnerHTML={{
                                    __html: highlightedOriginal,
                                  }}
                                ></code>
                              );
                            } else {
                              content = (
                                <code
                                  className="d-block text-start font-monospace flex-grow-1 text-black"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {linha.linha}
                                </code>
                              );
                            }

                            return (
                              <tr key={linha.numero}>
                                <td className="px-2 py-1 text-muted">
                                  {linha.numero}
                                </td>
                                <td
                                  className={`d-flex justify-content-between align-items-center w-100 px-2 py-1 ${
                                    linha.tipo === "original"
                                      ? "bg-danger bg-opacity-25"
                                      : linha.tipo === "modified"
                                      ? "bg-success bg-opacity-25"
                                      : "bg-light"
                                  }`}
                                >
                                  {content}
                                  <div className="d-flex gap-2">
                                    {linha.tipo !== "context" && (
                                      <button
                                        onClick={() =>
                                          deleteLine(linha, "original")
                                        }
                                        className="btn btn-danger text-white btn-sm rounded opacity-75"
                                      >
                                        ×
                                      </button>
                                    )}
                                    {linha.tipo !== "context" &&
                                      isFirstInGroup(i, "original") && (
                                        <button
                                          onClick={() =>
                                            replaceContent(i, i, "right")
                                          }
                                          className="btn btn-secondary text-white btn-sm rounded opacity-75"
                                        >
                                          →
                                        </button>
                                      )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <h4 className="font-bold mb-2 d-flex justify-content-between align-items-center">
                      Modified Code
                      <div className="d-flex gap-3">
                        <button
                          onClick={() => copyToClipboard(modifiedCode)}
                          className="btn btn-secondary text-white btn-sm rounded"
                        >
                          Copiar
                        </button>
                        <button
                          onClick={() =>
                            downloadCode(
                              modifiedCode,
                              "modified." + localStorage.getItem("devEXT")
                            )
                          }
                          className="btn btn-primary text-white btn-sm rounded"
                        >
                          Download Modified
                        </button>
                      </div>
                    </h4>
                    <div className="table-responsive">
                      <table className="table table-bordered table-sm">
                        <tbody className="modified-body">
                          {modifiedCode.linhas.map((linha, j) => {
                            const origLine = originalCode.linhas[j] || {
                              linha: "",
                            }; // Ensuring a fallback

                            let content;
                            if (
                              linha.tipo === "modified" ||
                              linha.tipo === "original"
                            ) {
                              // Check if the line has been identified as modified or original
                              const { highlightedModified } =
                                highlightDifferences(
                                  origLine.linha,
                                  linha.linha
                                );
                              content = (
                                <code
                                  className="d-block text-start font-monospace flex-grow-1 text-black"
                                  style={{ whiteSpace: "pre-wrap" }}
                                  dangerouslySetInnerHTML={{
                                    __html: highlightedModified,
                                  }}
                                ></code>
                              );
                            } else {
                              content = (
                                <code
                                  className="d-block text-start font-monospace flex-grow-1 text-black"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {linha.linha}
                                </code>
                              );
                            }

                            return (
                              <tr key={linha.numero}>
                                <td className="px-2 py-1 text-muted">
                                  {linha.numero}
                                </td>
                                <td
                                  className={`d-flex justify-content-between align-items-center w-100 px-2 py-1 ${
                                    linha.tipo === "modified"
                                      ? "bg-success bg-opacity-25"
                                      : "bg-light"
                                  }`}
                                >
                                  {content}
                                  <div className="d-flex gap-2">
                                    {linha.tipo !== "context" && (
                                      <button
                                        onClick={() =>
                                          deleteLine(linha, "modified")
                                        }
                                        className="btn btn-danger text-white btn-sm rounded opacity-75"
                                      >
                                        ×
                                      </button>
                                    )}
                                    {linha.tipo !== "context" &&
                                      isFirstInGroup(j, "modified") && (
                                        <button
                                          onClick={() =>
                                            replaceContent(j, j, "left")
                                          }
                                          className="btn btn-secondary text-white btn-sm rounded opacity-75"
                                        >
                                          ←
                                        </button>
                                      )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  onClick={closeModal}
                  className="btn btn-secondary text-white rounded"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeDiffModal;
